/**
 * Main billing hook that combines all billing-related hooks
 * Provides a centralized interface for billing operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import type {
  ValidateBillingResponse,
  BillingSettings,
} from '@/types/billing';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { backendErrorText } from '@/lib/translateBackendError';

// Re-export all contract hooks
export {
  contractKeys,
  useContracts,
  useContract,
  useCreateContract,
  useUpdateContract,
  useDeleteContract,
  useUpdateContractPayers,
  useUpdateContractField,
  useContractWithDetails,
} from './useContracts';

// Re-export all invoice hooks
export {
  invoiceKeys,
  useInvoices,
  useInvoice,
  useInvoiceSplits,
  useGenerateInvoices,
  usePreviewInvoices,
  useUpdateInvoiceStatus,
  useSendInvoice,
  useDownloadInvoicePDF,
  useUpdateInvoiceField,
  useInvoiceWithDetails,
} from './useInvoices';

// Re-export all tariff hooks
export {
  tariffKeys,
  useTariffs,
  useTariff,
  useActiveTariff,
  useTariffRates,
  useCreateTariff,
  useUpdateTariffRates,
  useActivateTariff,
  useArchiveTariff,
  useTariffWithRates,
} from './useTariffs';

// Combined billing keys for backward compatibility and unified access
export const billingKeys = {
  all: ['billing'] as const,
  validation: (childId?: string, contractId?: string) => 
    [...billingKeys.all, 'validation', { childId, contractId }] as const,
  // Org-scoped: an org switch does not clear the cache.
  settings: (orgId: string | null | undefined) => [...billingKeys.all, 'settings', orgId ?? null] as const,
  dashboard: () => [...billingKeys.all, 'dashboard'] as const,
  stats: (year?: number, month?: number) => 
    [...billingKeys.all, 'stats', { year, month }] as const,
};

// Hook to validate billing configuration
export function useValidateBilling(childId?: string, contractId?: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: billingKeys.validation(childId, contractId),
    queryFn: async () => {
      // This would call a validation endpoint
      // For now, return a mock response
      // #1156 dead-write repair: `billing_ValidateBillingResponse` declares
      // `is_valid` (snake_case, straight off the wire DTO). The previous
      // `isValid` key meant every consumer reading `is_valid` saw `undefined`.
      const response: ValidateBillingResponse = {
        is_valid: true,
        issues: [],
      };
      return response;
    },
    enabled: isSignedIn && isAuthReady && (!!childId || !!contractId),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to get billing settings
export function useBillingSettings() {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: billingKeys.settings(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      const { OrganisationSettingsService } = await import('@/api');
      const response = await OrganisationSettingsService.getApiV1OrganisationsBillingSettings(orgId);
      return response as BillingSettings;
    },
    enabled: isSignedIn && isAuthReady && !!orgId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Hook to update billing settings. `errorToast: false` leaves the error to the
// caller (the BESR-ID confirmation shows it inline, #1756).
export function useUpdateBillingSettings({ errorToast = true }: { errorToast?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const { orgId } = useAuth();
  
  return useMutation({
    mutationFn: async (settings: BillingSettings) => {
      if (!orgId) throw new Error('No organisation ID available');
      const { OrganisationSettingsService } = await import('@/api');
      const response = await OrganisationSettingsService.putApiV1OrganisationsBillingSettings(orgId, settings);
      return response as BillingSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(billingKeys.settings(orgId), data);
      queryClient.invalidateQueries({ queryKey: billingKeys.settings(orgId) });
      toast.success(i18n._('billing.toast.settingsUpdateSuccess'));
    },
    onError: (error: unknown) => {
      console.error("Billing settings update error:", error);
      if (!errorToast) return;
      // backendErrorText so a refused BESR-ID change (409 QRR_SCHEME_LOCKED)
      // shows its own message instead of the generic one.
      toast.error(backendErrorText(error, i18n, msg`Failed to update billing settings`));
    },
  });
}

// Hook to get billing dashboard data.
// DEAD: #1185 deleted the unmounted billing-dashboard subtree that was its only
// consumer. Kept (returning hard-coded zeros) because removing it was explicitly
// out of scope for that chore; use useBillingStats for real figures.
export function useBillingDashboard(_year?: number, _month?: number) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: billingKeys.dashboard(),
    queryFn: async () => {
      // This would aggregate data from contracts, invoices, and tariffs
      // For now, return mock dashboard data
      return {
        activeContracts: 0,
        totalRevenue: 0,
        pendingInvoices: 0,
        overdueInvoices: 0,
        recentActivity: [],
      };
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook to get billing statistics from the real org-wide aggregate endpoint.
// Replaces the former zero-returning mock: one cheap `/billing/stats` call
// supplies revenue / average-invoice / unpaid-count / active-contract figures.
// DEAD: #1185 deleted the unmounted billing-dashboard subtree that was its only
// consumer, and no surface has picked it up since. The docstring in
// cockpit/explorer/hooks/useAnalyticsExplorer.ts names it as a reuse candidate,
// but that panel never wired it up. Kept for the same reason as
// useBillingDashboard: removal was out of scope for that chore.
export function useBillingStats(year?: number, month?: number) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  const currentYear = year || new Date().getFullYear();
  const currentMonth = month || new Date().getMonth() + 1;

  return useQuery({
    queryKey: billingKeys.stats(currentYear, currentMonth),
    queryFn: async () => {
      const { BillingService } = await import('@/api');
      return BillingService.getApiV1BillingStats(currentYear, currentMonth);
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Utility hook to check if billing is properly configured
export function useBillingConfigured() {
  const { data: settings, isLoading: settingsLoading } = useBillingSettings();
  const { data: tariffs, isLoading: tariffsLoading } = useTariffs();
  
  const isLoading = settingsLoading || tariffsLoading;
  // Dead-read repair (#1156): `useTariffs()` resolves to a `TariffVersion[]`, not a
  // `{ tariffs: [] }` envelope, so `tariffs.tariffs?.length` was always `undefined`
  // — `isConfigured` could never become true and `missingConfiguration.tariffs`
  // could never become true either. Read the array's own length.
  const isConfigured = !isLoading && !!settings && !!tariffs && tariffs.length > 0;

  return {
    isConfigured,
    isLoading,
    missingConfiguration: {
      settings: !settings,
      tariffs: !tariffs || tariffs.length === 0,
    },
  };
}

// Import from useTariffs to make the function available
import { useTariffs } from './useTariffs';

// Helper hook for common billing operations
export function useBillingOperations() {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  
  const invalidateAllBilling = () => {
    queryClient.invalidateQueries({ queryKey: ['contracts'] });
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    queryClient.invalidateQueries({ queryKey: ['tariffs'] });
    queryClient.invalidateQueries({ queryKey: ['billing'] });
  };
  
  const refreshBillingData = async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['contracts'] }),
      queryClient.refetchQueries({ queryKey: ['invoices'] }),
      queryClient.refetchQueries({ queryKey: ['tariffs'] }),
    ]);
    toast.success(i18n._('billing.toast.dataRefreshed'));
  };
  
  return {
    invalidateAllBilling,
    refreshBillingData,
  };
}